import type { TourEvent } from '@/features/events';
import { formatDayLabel } from '@/features/events';
import { Section } from '@/components/layout';
import { ResponsiveImage } from '@/components/media';
import { OrnamentIcon, SacredGeometry } from '@/components/ornaments';
import { Reveal } from '@/components/motion';
import { mexicoMedia } from '../media';
import { MEXICO_COPY } from '../copy';
import { ArrowRule } from '../ornaments/ArrowRule';
import styles from './TriadaMexico.module.css';

/** Un icono por día, en el orden del mockup: ojo/sol concéntrico y estrella. */
const DAY_ICONS = ['eye', 'star'] as const;

export interface TriadaMexicoProps {
  event: TourEvent;
}

/**
 * S4 · TRES COLUMNAS — banda y 1027-1383 del mockup. La sección más compleja.
 *
 * Tres columnas de ancho casi igual, SIN gutter (los bordes se rozan) y con
 * TRES FONDOS DISTINTOS: aqua pálido con foto al pie · fotografía de la Cámara
 * Cuántica · crema con vegetación. Que cada una conserve su fondo propio es lo
 * que impide que la sección se lea como "tres tarjetas genéricas".
 *
 * Los días, sus rótulos y sus títulos salen de `event.program`.
 */
export function TriadaMexico({ event }: TriadaMexicoProps) {
  const { triada } = MEXICO_COPY;

  return (
    <Section width="full" spacing="flush" background="none" className={styles.section}>
      <div className={styles.triad}>
        {/* ---------- Columna 1 · un viaje de dos días ---------- */}
        <section className={styles.viaje} aria-labelledby="mexico-viaje">
          <ResponsiveImage
            asset={mexicoMedia.journeySkyline}
            sizes="(min-width: 64rem) 34vw, 100vw"
            className={styles.viajePhoto}
            decorative
          />

          <div className={styles.viajeBody}>
            <h2 id="mexico-viaje" className={styles.viajeTitle}>
              <ArrowRule className={styles.arrow} />
              <span>{triada.viajeEyebrow}</span>
              <ArrowRule direction="left" className={styles.arrow} />
            </h2>

            <ol className={styles.days} role="list">
              {event.program.map((day, index) => (
                <li key={day.day} className={styles.day}>
                  <OrnamentIcon name={DAY_ICONS[index] ?? 'star'} size={34} className={styles.dayIcon} />
                  <div className={styles.dayBody}>
                    <p className={styles.dayLabel}>{formatDayLabel(day)}</p>
                    <h3 className={styles.dayTitle}>{day.shortTitle ?? day.title}</h3>
                    <p className={styles.dayText}>{day.title}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ---------- Columna 2 · Cámara Cuántica ---------- */}
        <section className={styles.camara} aria-labelledby="mexico-camara">
          <ResponsiveImage
            asset={mexicoMedia.quantumChamber}
            sizes="(min-width: 64rem) 34vw, 100vw"
            className={styles.camaraPhoto}
          />

          <div className={styles.camaraBody}>
            <SacredGeometry variant="metatron" size={54} className={styles.camaraMark} />
            <h2 id="mexico-camara" className={styles.camaraTitle}>
              {triada.camaraTitle}
            </h2>
            <p className={styles.camaraText}>{triada.camaraBody}</p>
          </div>
        </section>

        {/* ---------- Columna 3 · Azrahel ---------- */}
        <section className={styles.azrahel} aria-labelledby="mexico-azrahel">
          <Reveal className={styles.azrahelBody}>
            <h2 id="mexico-azrahel" className={styles.azrahelName}>
              {triada.azrahelName}
            </h2>
            <p className={styles.azrahelRole}>{triada.azrahelRole}</p>

            <div className={styles.portraitFrame}>
              <ResponsiveImage
                asset={mexicoMedia.leafFrame}
                sizes="(min-width: 64rem) 34vw, 100vw"
                className={styles.portraitBg}
                decorative
              />
              <ResponsiveImage
                asset={mexicoMedia.heroPortrait}
                sizes="(min-width: 64rem) 26vw, 70vw"
                className={styles.portrait}
              />
            </div>

            <p className={styles.azrahelBio}>{triada.azrahelBio}</p>
          </Reveal>
        </section>
      </div>
    </Section>
  );
}
